import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Plus, Edit, Trash2, Shield, User, Users as UsersIcon, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../../services/api';

const CHURCHES = [
  { id: 'karlskrona', name: 'Karlskrona' },
  { id: 'karlshamn', name: 'Karlshamn' },
  { id: 'olofstrom', name: 'Olofström' }
];

const MODULES = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'heroes', label: 'Heroes' },
  { id: 'news', label: 'Nyheter' },
  { id: 'events', label: 'Kungörelser' },
  { id: 'photos', label: 'Foton' },
  { id: 'bible', label: 'Bibelläsningar' },
  { id: 'prayers', label: 'Böner' },
  { id: 'mass_schedules', label: 'Mässcheman' },
  { id: 'languages', label: 'Språkinställningar' },
  { id: 'users', label: 'Användare' }
];

const ROLES = [
  { id: 'super_admin', label: 'Super Admin', description: 'Full access to all modules and churches' },
  { id: 'church_admin', label: 'Church Admin', description: 'Manage assigned churches' },
  { id: 'editor', label: 'Editor', description: 'Limited editing rights' }
];

const ROLE_DEFAULT_PERMISSIONS = {
  super_admin: {
    dashboard: true, heroes: true, news: true, events: true, photos: true,
    bible: true, prayers: true, mass_schedules: true, languages: true, users: true
  },
  church_admin: {
    dashboard: true, heroes: false, news: true, events: true, photos: true,
    bible: false, prayers: false, mass_schedules: true, languages: false, users: false
  },
  editor: {
    dashboard: true, heroes: false, news: true, events: false, photos: true,
    bible: false, prayers: false, mass_schedules: false, languages: false, users: false
  }
};

const UsersManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'editor',
    assigned_churches: [],
    permissions: {},
    display_name: '',
    email: ''
  });
  const [showCustomPermissions, setShowCustomPermissions] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await api.getUsers();
      setUsers(data);
    } catch (error) {
      toast.error('Kunde inte ladda användare');
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      role: 'editor',
      assigned_churches: [],
      permissions: {},
      display_name: '',
      email: ''
    });
    setEditingUser(null);
    setShowCustomPermissions(false);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '', // Don't show password
      role: user.role,
      assigned_churches: user.assigned_churches || [],
      permissions: user.permissions || {},
      display_name: user.display_name || '',
      email: user.email || ''
    });
    setShowCustomPermissions(Object.keys(user.permissions || {}).length > 0);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update user
        const updateData = {
          role: formData.role,
          assigned_churches: formData.assigned_churches,
          permissions: showCustomPermissions ? formData.permissions : {},
          display_name: formData.display_name,
          email: formData.email
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await api.updateUser(editingUser.id, updateData);
        toast.success('Användare uppdaterad!');
      } else {
        // Create user
        if (!formData.password) {
          toast.error('Lösenord krävs för nya användare');
          return;
        }
        await api.createUser({
          ...formData,
          permissions: showCustomPermissions ? formData.permissions : {}
        });
        toast.success('Användare skapad!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Ett fel uppstod';
      toast.error(message);
    }
  };

  const handleDelete = async (userId, username) => {
    if (window.confirm(`Är du säker på att du vill radera användaren "${username}"?`)) {
      try {
        await api.deleteUser(userId);
        toast.success('Användare raderad!');
        loadUsers();
      } catch (error) {
        const message = error.response?.data?.detail || 'Kunde inte radera användare';
        toast.error(message);
      }
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.updateUser(user.id, { active: !user.active });
      toast.success(user.active ? 'Användare inaktiverad' : 'Användare aktiverad');
      loadUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Kunde inte ändra status';
      toast.error(message);
    }
  };

  const toggleChurch = (churchId) => {
    const current = formData.assigned_churches;
    if (current.includes(churchId)) {
      setFormData({ ...formData, assigned_churches: current.filter(c => c !== churchId) });
    } else {
      setFormData({ ...formData, assigned_churches: [...current, churchId] });
    }
  };

  const togglePermission = (moduleId) => {
    const current = formData.permissions;
    setFormData({
      ...formData,
      permissions: { ...current, [moduleId]: !current[moduleId] }
    });
  };

  const getEffectivePermissions = (role, customPermissions = {}) => {
    const base = ROLE_DEFAULT_PERMISSIONS[role] || ROLE_DEFAULT_PERMISSIONS.editor;
    return { ...base, ...customPermissions };
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-100 text-purple-800';
      case 'church_admin': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Laddar användare...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Användare</h1>
          <p className="text-gray-600 mt-1">Hantera administratörer och deras behörigheter</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-user-button">
              <Plus className="w-4 h-4 mr-2" />
              Lägg till användare
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Redigera användare' : 'Skapa ny användare'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="user-form">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Användarnamn *</label>
                  <Input
                    required
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    disabled={!!editingUser}
                    data-testid="user-username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {editingUser ? 'Nytt lösenord (lämna tomt för att behålla)' : 'Lösenord *'}
                  </label>
                  <Input
                    type="password"
                    required={!editingUser}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    data-testid="user-password"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Visningsnamn</label>
                  <Input
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    placeholder="Förnamn Efternamn"
                    data-testid="user-display-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">E-post</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                    data-testid="user-email"
                  />
                </div>
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-sm font-medium mb-3">Roll *</label>
                <div className="grid grid-cols-1 gap-3">
                  {ROLES.map((role) => (
                    <div
                      key={role.id}
                      onClick={() => setFormData({ ...formData, role: role.id })}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.role === role.id
                          ? 'border-yellow-500 bg-yellow-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`role-${role.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{role.label}</h4>
                          <p className="text-sm text-gray-600">{role.description}</p>
                        </div>
                        {formData.role === role.id && (
                          <Check className="w-5 h-5 text-yellow-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Church Assignment (for non-super_admin) */}
              {formData.role !== 'super_admin' && (
                <div>
                  <label className="block text-sm font-medium mb-3">Tilldelade församlingar</label>
                  <div className="flex flex-wrap gap-2">
                    {CHURCHES.map((church) => (
                      <button
                        key={church.id}
                        type="button"
                        onClick={() => toggleChurch(church.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          formData.assigned_churches.includes(church.id)
                            ? 'bg-yellow-500 text-gray-900'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        data-testid={`church-${church.id}`}
                      >
                        {church.name}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Välj vilka församlingar användaren kan hantera
                  </p>
                </div>
              )}

              {/* Custom Permissions Toggle */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-900">Anpassade behörigheter</label>
                    <p className="text-xs text-gray-500">Åsidosätt standardbehörigheter för rollen</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCustomPermissions(!showCustomPermissions)}
                    className={`px-3 py-1 rounded text-sm ${
                      showCustomPermissions ? 'bg-yellow-500 text-gray-900' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {showCustomPermissions ? 'Aktiverat' : 'Inaktiverat'}
                  </button>
                </div>

                {showCustomPermissions && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {MODULES.map((module) => {
                      const defaultVal = ROLE_DEFAULT_PERMISSIONS[formData.role]?.[module.id] || false;
                      const customVal = formData.permissions[module.id];
                      const isCustomized = customVal !== undefined;
                      const currentVal = isCustomized ? customVal : defaultVal;

                      return (
                        <button
                          key={module.id}
                          type="button"
                          onClick={() => togglePermission(module.id)}
                          className={`px-3 py-2 rounded text-sm text-left flex items-center justify-between ${
                            currentVal
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          } ${isCustomized ? 'ring-2 ring-yellow-400' : ''}`}
                        >
                          <span>{module.label}</span>
                          {currentVal ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <Button type="submit" className="w-full" data-testid="user-submit">
                {editingUser ? 'Uppdatera användare' : 'Skapa användare'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users List */}
      <div className="grid grid-cols-1 gap-4">
        {users.map((user) => (
          <Card key={user.id} className={!user.active ? 'opacity-60' : ''} data-testid={`user-card-${user.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    user.role === 'super_admin' ? 'bg-purple-100' :
                    user.role === 'church_admin' ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    {user.role === 'super_admin' ? (
                      <Shield className="w-6 h-6 text-purple-600" />
                    ) : user.role === 'church_admin' ? (
                      <UsersIcon className="w-6 h-6 text-blue-600" />
                    ) : (
                      <User className="w-6 h-6 text-gray-600" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-bold text-gray-900">
                        {user.display_name || user.username}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                        {ROLES.find(r => r.id === user.role)?.label || user.role}
                      </span>
                      {!user.active && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Inaktiv
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">@{user.username}</p>
                    {user.email && <p className="text-sm text-gray-500">{user.email}</p>}
                    
                    {/* Churches */}
                    {user.role !== 'super_admin' && user.assigned_churches?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {user.assigned_churches.map(churchId => (
                          <span key={churchId} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                            {CHURCHES.find(c => c.id === churchId)?.name || churchId}
                          </span>
                        ))}
                      </div>
                    )}
                    {user.role === 'super_admin' && (
                      <p className="text-xs text-purple-600 mt-1">Tillgång till alla församlingar</p>
                    )}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleActive(user)}
                    data-testid={`toggle-active-${user.id}`}
                  >
                    {user.active ? 'Inaktivera' : 'Aktivera'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(user)}
                    data-testid={`edit-user-${user.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(user.id, user.username)}
                    data-testid={`delete-user-${user.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {users.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <UsersIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">Inga användare</h3>
              <p className="text-gray-500 mt-1">Klicka på &quot;Lägg till användare&quot; för att skapa den första</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default UsersManager;
